<template>
  <div class="card mb-4">

    <!-- UPDATED: Added flex-column and responsive alignment classes -->
    <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
      <h6 class="mb-0">User Group</h6>
      <div class="d-flex align-items-center w-100 w-md-auto justify-content-between justify-content-md-end flex-wrap gap-2">
        <!-- VIEW TOGGLE BUTTONS -->
        <div class="btn-group btn-group-sm me-md-3 shadow-sm">
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'table' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'card' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'card'"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>

        <button type="button" class="btn btn-theme-primary mb-0 flex-grow-1 flex-md-grow-0" @click="showModal('add')">
          ADD USER GROUP
        </button>
      </div>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      
      <!-- 1. TABLE VIEW -->
      <div v-if="viewMode === 'table'" class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">User Group name
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{
                $t('status') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{
                $t('action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(role, index) in paginatedRoles" :key="index">

              <td class="text-left">
               <p class="text-xs font-weight-bold mb-0 text-dark">{{ role.user_group_name }}</p>
              </td>
              <td class="text-left">
                <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">{{
                  role.status == 1 ? 'Active' : "InActive" }}</soft-badge>
              </td>
              <td class="text-left">
                <a href="javascript:;" class="text-secondary font-weight-bold text-xs"
                  @click="showModal('edit', (currentPage - 1) * itemsPerPage + index)">
                  <i class="fas fa-edit"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 2. CARD VIEW -->
      <div v-else class="px-3 px-md-4 pb-4 pt-3">
        <div class="row">
          <!-- UPDATED: Added col-12 for full width on mobile -->
          <div v-for="(role, index) in paginatedRoles" :key="'card-' + index" class="col-12 col-md-6 col-xl-3 mb-4">
            <div class="card h-100 border shadow-sm">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div class="d-flex align-items-center overflow-hidden">
                    <!-- Icon background synced with theme -->
                    <div class="icon-shape icon-sm shadow text-center border-radius-md d-flex align-items-center justify-content-center me-2 theme-icon-bg flex-shrink-0" style="width: 32px; height: 32px;">
                      <i class="fas fa-users text-white text-xs opacity-10"></i>
                    </div>
                   <h6 class="mb-0 text-sm text-dark text-truncate">{{ role.user_group_name }}</h6>
                  </div>
                  <a href="javascript:;" class="text-secondary" @click="showModal('edit', (currentPage - 1) * itemsPerPage + index)">
                    <i class="fas fa-edit text-xs"></i>
                  </a>
                </div>
                
                <div class="d-flex justify-content-between align-items-center pt-3 border-top">
                  <span class="text-xxs text-uppercase text-secondary font-weight-bolder">{{ $t('status') }}</span>
                  <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">
                    {{ role.status == 1 ? 'Active' : "InActive" }}
                  </soft-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results Logic -->
      <div v-if="roles.length === 0" class="text-center py-4 text-secondary text-sm">
        No user groups found.
      </div>

      <!-- UPDATED: Added flex-wrap for mobile pagination -->
      <div class="pagination flex-wrap">
        <button @click="prevPage" :disabled="currentPage === 1">{{ $t('previous') }}</button>
        <button v-for="page in totalPages" :key="page" @click="goToPage(page)"
          :class="{ active: currentPage === page }">
          {{ page }}
        </button>
        <button @click="nextPage" :disabled="currentPage === totalPages">{{ $t('next') }}</button>
      </div>
    </div>

    <!-- Add Role Modal -->
    <div v-if="isAddModalVisible" class="modal fade show" style="display: block;" tabindex="-1" role="dialog"
      aria-labelledby="addRoleModalLabel" aria-hidden="true">
      <!-- UPDATED: Added modal-dialog-centered and responsive padding -->
      <div class="modal-dialog modal-dialog-centered px-3" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="addRoleModalLabel">Add User Group</h5>
            <button type="button" class="close btn-modal-close" @click="hideModal('add')" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <!-- UPDATED: Added max-height for mobile scrolls -->
          <div class="modal-body" style="max-height: 80vh; overflow-y: auto;">
            <form @submit.prevent="addRole">


              <div class="row">
                <div class="form-group col-md-12">
                  <label for="user_group_name">User Group Name</label>
                  <input type="text" id="user_group_name" v-model="newRole.user_group_name" class="form-control"
                    placeholder="Enter user group name" />
                  <span v-if="errors.user_group_name" class="text-danger">{{ errors.user_group_name }}</span>
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-group">
                  <label for="user_id">Device List</label>
                  <multiselect v-model="newRole.user_id" :options="users" :multiple="true" :close-on-select="false"
                    :clear-on-select="false" :preserve-search="true" placeholder="Pick some" label="name"
                    track-by="name"></multiselect>
                  <span v-if="errors.user_id" class="text-danger">{{ errors.user_id }}</span>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col-md-6"></div>
                <div class="col-md-6 col-12 d-flex justify-content-center justify-content-md-end">
                  <div class="form-group">
                    <label class="mr-2">Inactive</label>
                    <label class="switch">
                      <input type="checkbox" v-model="newRole.status" :true-value="'1'" :false-value="'0'">
                      <span class="slider round"></span>
                    </label>
                    <label class="ml-2">Active</label>
                  </div>
                </div>

              </div>


              <button type="submit" class="btn btn-theme-primary mt-3 w-100">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Add Group</span>
              </button>

            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Role Modal -->
    <div v-if="isEditModalVisible" class="modal fade show" style="display: block;" tabindex="-1" role="dialog"
      aria-labelledby="editRoleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered px-3" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editRoleModalLabel">Update Device Group</h5>
            <button type="button" class="close btn-modal-close" @click="hideModal('edit')" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" style="max-height: 80vh; overflow-y: auto;">
            <form @submit.prevent="updateRole">

              <div class="row">

                <div class="form-group col-md-12">
                  <label for="user_group_name">Group Name</label>
                  <input type="text" id="user_group_name" v-model="editRole.user_group_name" class="form-control"
                    placeholder="Enter user group name" />
                  <small v-if="errors.edit_user_group_name" class="text-danger">{{ errors.edit_user_group_name
                    }}</small>
                </div>

              </div>

              <div class="col-md-12">
                <div class="form-group">
                  <label for="user_id">Device List</label>
                  <multiselect v-model="editRole.user_id" :options="users" :multiple="true" :close-on-select="false"
                    :clear-on-select="false" :preserve-search="true" placeholder="Pick some" label="name"
                    track-by="name"></multiselect>

                </div>
              </div>

              <div class="row">

                <div class="col-md-6 col-12 mx-auto">
                  <div class="form-group d-flex align-items-center justify-content-center">
                    <label class="mr-2">Inactive</label>
                    <label class="switch">
                      <input type="checkbox" v-model="roles[selectedIndex].status" :true-value="'1'" :false-value="'0'">
                      <span class="slider round"></span>
                    </label>
                    <label class="ml-2">Active</label>
                  </div>
                </div>
              </div>

              <button type="submit" class="btn btn-theme-primary w-100">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Update Group</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>
<script>
import resourcePhotoUrl from "@/assets/img/resource.png";
import SoftBadge from "@/components/SoftBadge.vue";
import '@fortawesome/fontawesome-free/css/all.css';
import UserGroupDataService from "../../services/UserGroupDataService.js";
import UsersDataService from "../../services/UsersDataService.js";
import Swal from 'sweetalert2';
import Multiselect from 'vue-multiselect';

export default {
  name: "UserGroup",
  components: {
    SoftBadge,
    Multiselect
  },
  data() {
    return {
      viewMode: 'card', 
      isLoading: false,
      roles: [],
      users: [],
      newRole: {
        user_group_name: "",
        user_id: "",
        status: 'Inactive'
      },
      editRole: {
        user_group_name: "",
        user_id: [],
        status: "Inactive"
      },
      resourcePhotoUrl,
      selectedIndex: null,
      isAddModalVisible: false,
      isEditModalVisible: false,
      selectedRoles: [], // For selecting roles in add mode
      currentPage: 1,
      itemsPerPage: 10,
      selectedLanguage: this.$i18n.locale,
      isImageSelected: true,
      isVideoSelected: false,
      errors: {}
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.roles.length / this.itemsPerPage);
    },
    paginatedRoles() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.roles.slice(start, end);
    },
  },
  created() {
    this.getAllUserGroup();
    this.getAllUser();
  },
  methods: {
    onTypeChange() {
      this.roles[this.selectedIndex].user_group_name = "";
      this.roles[this.selectedIndex].user_id = "";
    },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
    },
    validateFields() {
      this.errors = {}; // Reset errors

      if (!this.newRole.user_group_name) {
        this.errors.user_group_name = "Group name is required.";
      }

      if (!this.newRole.user_id) {
        this.errors.user_id = "Please select a device name.";
      }

      return Object.keys(this.errors).length === 0; // Returns true if no errors
    },
    getAllUserGroup() {
      UserGroupDataService.getAll()
        .then(response => {
          this.roles = response.data;
          console.log(this.roles);
        })
        .catch(e => {
          console.log(e);
        });
    },
    getAllUser() {
      UsersDataService.getAll()
        .then(response => {
          this.users = response.data.map(user => ({
            id: user.id,
            name: user.name
          }));
          console.log(this.users);
        })
        .catch(e => {
          console.log(e);
        });
    },
    addRole() {

      if (!this.validateFields()) {
        this.isLoading = false;
        return;
      }
      const payload = {
        user_group_name: this.newRole.user_group_name,
        user_id: this.newRole.user_id.map(device => device.id),
        status: this.newRole.status
      };
      console.log(payload);

      try {
        UserGroupDataService.create(payload)
          .then(response => {
            console.log(response.data.usergroup);

            if (response.data.usergroup) {
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'User group added successfully!',
                confirmButtonText: 'OK'
              }).then(() => {
                this.getAllUserGroup();
              });
            }

          })
          .catch(e => {
            this.isLoading = false;
            console.log(e);
          });

      } catch (error) {
        console.error("Upload failed:", error);
        alert("Error uploading file.");
      }
      this.resetForm('add');
    },
    showModal(mode, index = null) {
      if (mode === 'add') {
        this.isAddModalVisible = true;
        this.selectedRoles = [];
        this.newRole = { user_group_name: "", user_id: "", status: "1" };
      } else if (mode === 'edit' && index !== null) {
        this.selectedIndex = index;
        this.isEditModalVisible = true;

        this.editRole = {
          user_group_name: this.roles[index].user_group_name,
          user_id: this.roles[index].users.map(device => ({
            id: device.id,
            name: device.name
          })),
          status: this.roles[index].status
        };

      }
      this.isLoading = false;
    },
    onChangeErrorClear() {
      this.errors = {};
    },
    hideModal(mode) {
      if (mode === 'add') {
        this.isAddModalVisible = false;
      } else if (mode === 'edit') {
        this.isEditModalVisible = false;
      }
      this.selectedIndex = null;
      this.isLoading = false;
      this.errors = {};
    },
    updateRole() {
      this.errors = {};
      this.isLoading = true;
      const updatedRole = { ...this.roles[this.selectedIndex] };
      console.log(updatedRole);

      // Validation Checks
      if (!this.editRole.user_group_name) {
        this.errors.user_group_name = "Media name is required.";
      }

      if (!this.editRole.user_id || this.editRole.user_id.length === 0) {
        this.errors.user_id = "Please select at least one device.";
      }

      console.log(Object.keys(this.errors));
      // If there are validation errors, stop submission
      if (Object.keys(this.errors).length > 0) {
        this.isLoading = false;
        return;
      }

      const payload = {
        user_group_name: this.editRole.user_group_name,
        user_id: this.editRole.user_id.map(device => device.id),
        status: this.editRole.status
      };

      // Sending the data to the API for updating the media info
      UserGroupDataService.update(this.roles[this.selectedIndex].id, payload)
        .then((response) => {
          console.log(response);
          if (response.status) {
            this.isEditModalVisible = false;
            Swal.fire({
              icon: 'success',
              title: 'Success',
              text: 'User group updated successfully!',
              confirmButtonText: 'OK',
            }).then(() => {
              this.getAllUserGroup();
            });
          }
        })
        .catch((e) => {
          this.isLoading = false;
          console.log(e);

          if (e.response && e.response.data && e.response.data.errors) {
            e.response.data.errors.forEach((error) => {
              if (error.code === "user_group_name") {
                this.errors.edit_user_group_name = error.message;
              }
            });
          }
        });
    },
    resetForm(mode) {
      if (mode === 'add') {
        this.newRole = { user_group_name: "", user_id: "", status: "Active" };
        this.selectedRoles = [];
        this.isAddModalVisible = false;
      } else if (mode === 'edit') {
        this.selectedIndex = null;
        this.isEditModalVisible = false;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  },
};
</script>

<style scoped>
/* ==========================
   DYNAMIC THEME SYNC
========================== */
.btn-theme-primary {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: 1px solid var(--bs-primary) !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}

.btn-theme-primary:hover {
  filter: brightness(0.9);
  transform: translateY(-1px);
}

.btn-primary {
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.btn-outline-primary {
  border: 1px solid var(--bs-primary) !important;
  color: var(--bs-primary) !important;
  background: transparent;
}

.btn-outline-primary:hover {
  background-color: var(--bs-primary) !important;
  color: white !important;
}

.theme-icon-bg {
  background-color: var(--bs-primary) !important;
}

.btn-modal-close {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ==========================
   PAGINATION SYNC
========================== */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  padding-bottom: 1rem;
}

.pagination button {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  margin: 0.5rem; /* Updated for wrapping */
  font-size: 12px;
  font-weight: 800 !important;
  text-transform: uppercase;
  cursor: pointer;
  transition: 0.2s;
}

.pagination button:disabled {
  background-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
}

.pagination button.active {
  background-color: #ffffff !important;
  color: var(--bs-primary) !important;
  border: 2px solid var(--bs-primary) !important;
}

/* ==========================
   SWITCH SYNC
========================== */
input:checked+.slider {
  background-color: var(--bs-primary) !important;
}

/* ==========================
   EXISTING LAYOUT LOGIC
========================== */
.langdrop {
  width: 130px;
  margin-left: 87%;
}

@media (min-width:300px) and (max-width:550px) {
  .langdrop {
    width: 130px;
    margin-left: 57%;
  }
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px; width: 26px;
  left: 4px; bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked+.slider:before {
  transform: translateX(26px);
}

.table th,
.table td {
  text-align: left;
  padding: 12px;
}

.modal.fade.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  margin-top: 10%;
}

.text-danger {
  font-size: 14px;
}

/* --- ADDED MOBILE RESPONSIVE CSS --- */
@media (max-width: 768px) {
  .card-header {
    padding-bottom: 20px !important;
  }
  .modal-dialog {
    margin: 0.5rem;
  }
}
</style>